import { nid } from '@astlai/common/util/nanoid';
import type { InlineQuote } from 'src/util/citation-transformer';

/** Tool call from the LLM during agentic loop, with optional result info */
export interface ToolCall {
	name: string;
	arguments: Record<string, unknown>;
	call_id: string;
	description?: string;
	/** Result count, set after tool execution completes */
	result_count?: number;
}

/** Topic extracted from the conversation */
export interface TopicInfo {
	topic_description: string;
}

/** Query classification from the classifier */
export interface ClassificationInfo {
	name: string;
	enable_thinking: boolean;
	enable_tools: boolean;
	temperature: number;
	max_iterations: number;
	thinking_budget: number;
}

export class ChatMessage {
	type?: 'message';
	status?:
		| 'pending'
		| 'rag.started'
		| 'rag.tool_call'
		| 'rag.tool_result'
		| 'rag.sources'
		| 'rag.web_sources'
		| 'reasoning'
		| 'in_progress'
		| 'completed'
		| 'incomplete';
	annotations?: any[];
	refusal?: boolean;

	id? = nid();

	/** Topic of the conversation (extracted post-hoc) */
	topic?: TopicInfo;

	/** Query classification result */
	classification?: ClassificationInfo;

	/** Current tool being called (index into toolCalls array) */
	currentToolCallIndex?: number;

	/** Tool calls with their results */
	toolCalls?: ToolCall[];

	timings?: {
		total_seconds: number;
		steps?: Record<string, number>;
	};

	/** Tools available when this message was sent (e.g. ['web_search']) */
	available_tools?: string[];

	created?: Date = new Date();

	constructor(
		public role: 'user' | 'assistant' | 'system',
		public content: string,
		public reasoning_content: string = ''
	) {}
}

export type ChatMessages = ChatMessage[];

/** Simplified source reference with just IDs (persisted format) */
export interface SourceDocument {
	doc_id: number;
	chunk_ids: number[];
}

export type WebSourceType = 'web' | 'fetched_url';

/** External source from web search or fetched URL (persisted format) */
export interface WebSourceDocument {
	id: string; // CRC32 hex hash of URL (8 chars), stable across fetches
	type: WebSourceType;
	title: string;
	url: string;
	description: string;
	content?: string; // Page content snippet (not displayed in UI, available for future agentic loops)
	fetched_at?: string;
}

interface WithCitationInfo {
	citation_index: number;
	citation_color: string;
	inlineQuotes: InlineQuote[];
}

/** Web source enriched with citation display info */
export interface QuotedWebSource extends WebSourceDocument, WithCitationInfo {}

/** Source document enriched with citation display info and inline quotes */
export interface QuotedDocument extends SourceDocument, WithCitationInfo {}

/** Chunk data sent during streaming.
 * Document metadata available via doc_id lookup in RetrievalContext.documents.
 */
export interface ChunkDTO {
	id: number;
	text: string;
	doc_id: number;
	chunk_type: string;
}

/** Full retrieval context sent during streaming (not persisted) */
export interface RetrievalContext {
	documents: DocumentOverview[];
	chunks: ChunkDTO[];
}

export type EntityType = 'debate' | 'person' | 'eu_cod' | 'cla';

export type DocumentType =
	| 'speech'
	| 'politician_bio'
	| 'eu_com_proposal'
	| 'eu_ep_amendments_adopted'
	| 'eu_ep_cmt_opinion'
	| 'eu_egp_briefing'
	| 'cla_paragraph'
	| 'cla_annex'
	| 'press_message';

export interface ResourceWithDocuments {
	// Simplified property names (new format)
	id?: number;
	type?: EntityType;
	name?: string;
	data?: Record<string, any>;
	created?: string; // ISO date string from Python
	date?: string | null; // ISO date string from Python (actual resource date, e.g., debate date)
	document_count: number;
}

export interface ResourcesResponse {
	resources: ResourceWithDocuments[];
	total_resources: number;
	total_documents: number;
	chunk_count: number;
	chunk_coverage: Record<string, number>;
	page: number;
	per_page: number;
}

export interface DocumentOverview {
	id: number;
	type: string;
	meta?: Record<string, string>;
	res_name?: string;
	res_date?: string;
}

export interface DocumentDetail extends DocumentOverview {
	text: string;
	summary: string;
	meta: Record<string, string>;
	created: string;
	res_href?: string;
	res_type?: string;
	res_id?: number;
}

export interface TenantStats {
	total_resources: number;
	total_documents: number;
	by_resource_type: Record<
		string,
		{
			count: number;
			data_keys: string[];
			documents: Record<string, { count: number; meta_keys: string[] }>;
		}
	>;
}

export interface ResourceDetail {
	id: number;
	type: string;
	name: string;
	data: Record<string, any>;
	created: string;
	date: string | null;
	documents: DocumentOverview[];
}
